import {
  Stack,
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React from "react";
import { StyledTooltip } from "./SubtopicCard";
import { InfoOutlined } from "@mui/icons-material";
import { singleLineEllipsis } from "../../Components/AllCards/layouts/CardHeader";

interface ClusterCardProps {
  setStatus: (status: string) => void;
  filters: {
    cluster_color?: string;
    cluster: {
      name: string;
      id: number | string;
      pending: number;
      save_for_later: number;
      reviewed: number;

      // metadata
      status: string;
      exposure_index: number | null;
      accuracy_index: number | null;
      retention_index: number | null;
      type: string;
      lod_index: number;
      for_super_start: boolean;
      primary_language: string;
      description: string;
      max_time: number;
      min_time: number;
      by_sme: boolean;
      is_active: boolean;
      created_at: string;
      created_by_id: number;
      modified_at: string;
      modified_by_id: number;
    };
    exam: string;
    goal: string;
    section: string;
    topic: string;
    subtopic: string;
  };
  selectedStatus: string;
  trashRef: any;
}

function ClusterCard({
  setStatus,
  filters,
  selectedStatus,
  trashRef,
}: ClusterCardProps) {
  const { cluster } = filters;
  console.log(filters, "filters");
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ borderRadius: "8px 8px 0 0" }}
    >
      <Card sx={{ mb: "8px", width: "100%" }}>
        <CardContent
          sx={{
            padding: "16px !important",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {/* Header row */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: "8px",
            }}
          >
            {/* Cluster name */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                style={{
                  background: filters.cluster_color ?? "#E57373",
                  marginRight: "8px",
                  borderRadius: "50%",
                  height: "7px",
                  width: "7px",
                  minHeight: "7px",
                  minWidth: "7px",
                }}
              />
              <Typography variant="h6" fontSize={"1rem"}>
                {cluster.name}{" "}
                <Chip
                  label={`id: #${cluster.id}`}
                  size="small"
                  sx={{
                    border: "1px solid rgb(0 0 0 / 60%)",
                    borderRadius: "4px",
                    padding: "0",
                    color: "rgb(0 0 0 / 60%)",
                    fontSize: "0.75rem",
                    background: "transparent",
                    "& .MuiChip-label": {
                      padding: "0 3px !important",
                    },
                    ...singleLineEllipsis,
                  }}
                />
              </Typography>{" "}
              <StyledTooltip
                arrow
                placement="top"
                title={
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      p: 2,
                      borderRadius: 2,
                      boxShadow: 1,
                      bgcolor: "background.paper",
                      maxWidth: 480,
                      minWidth: 320,
                    }}
                  >
                    {/* Header */}
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{ mb: 1, color: "text.primary" }}
                    >
                      {cluster.name} Details{" "}
                      <Chip
                        label={`id: #${cluster.id}`}
                        size="small"
                        sx={{
                          border: "1px solid rgb(0 0 0 / 60%)",
                          borderRadius: "4px",
                          padding: "0",
                          color: "rgb(0 0 0 / 60%)",
                          fontSize: "0.75rem",
                          background: "transparent",
                          "& .MuiChip-label": {
                            padding: "0 3px !important",
                          },
                          ...singleLineEllipsis,
                        }}
                      />
                    </Typography>

                    {/* Grid details */}
                    <Grid container spacing={1.5}>
                      <Grid item xs={4}>
                        <Typography variant="caption" fontWeight={500}>
                          Type
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {cluster.type}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" fontWeight={500}>
                          LOD Index
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {cluster.lod_index}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" fontWeight={500}>
                          Primary Language
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {cluster.primary_language}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="caption" fontWeight={500}>
                          Description
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {cluster.description}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="caption" fontWeight={500}>
                          Time
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {cluster.min_time} – {cluster.max_time}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" fontWeight={500}>
                          By SME
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {cluster.by_sme ? "Yes" : "No"}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" fontWeight={500}>
                          Exposure Index
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {cluster.exposure_index || "-"}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="caption" fontWeight={500}>
                          Accuracy Index
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {cluster.accuracy_index || "-"}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" fontWeight={500}>
                          Retention Index
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {cluster.retention_index || "-"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                }
              >
                <IconButton size="small">
                  <InfoOutlined fontSize="small" />
                </IconButton>
              </StyledTooltip>
            </Box>

            {/* Chips */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "8px",
                justifyContent: "flex-end",
              }}
            >
              {/* Pending */}
              <Chip
                onClick={(e) => {
                  e.stopPropagation();
                  setStatus("pending");
                }}
                label={`Pending: ${cluster.pending + cluster.save_for_later}`}
                sx={{
                  opacity: selectedStatus !== "pending" ? "0.5" : "1",
                  transition: "all 0.3s ease",
                  transform:
                    selectedStatus === "pending" ? "scale(1.08)" : "scale(1)",
                  ...(selectedStatus === "pending"
                    ? {
                        color: "white",
                        background: "#f97316",
                        border: "1px solid #f97316",
                        pointerEvents: "none",
                      }
                    : {
                        color: "#c2410c",
                        border: "1px solid #fdba74",
                        background: "#ffedd5",
                        ":hover": {
                          color: "white",
                          background: "#f97316",
                          border: "1px solid #f97316",
                        },
                      }),
                }}
                size="small"
              />

              {/* Save for later */}
              <Chip
                onClick={(e) => {
                  e.stopPropagation();
                  setStatus("save_for_later");
                }}
                label={`Saved for Later: ${cluster.save_for_later}`}
                sx={{
                  opacity: selectedStatus !== "save_for_later" ? "0.5" : "1",
                  transition: "all 0.3s ease",
                  transform:
                    selectedStatus === "save_for_later"
                      ? "scale(1.08)"
                      : "scale(1)",
                  ...(selectedStatus === "save_for_later"
                    ? {
                        color: "white",
                        background: "#eab308",
                        border: "1px solid #eab308",
                        pointerEvents: "none",
                      }
                    : {
                        color: "#a16207",
                        border: "1px solid #fde047",
                        background: "#fef9c3",
                        ":hover": {
                          color: "white",
                          background: "#eab308",
                          border: "1px solid #eab308",
                        },
                      }),
                }}
                size="small"
              />

              {/* Reviewed */}
              <Chip
                ref={trashRef}
                onClick={(e) => {
                  e.stopPropagation();
                  setStatus("reviewed");
                }}
                label={`Reviewed: ${cluster.reviewed}`}
                sx={{
                  opacity: selectedStatus !== "reviewed" ? "0.5" : "1",
                  transition: "all 0.3s ease",
                  transform:
                    selectedStatus === "reviewed" ? "scale(1.08)" : "scale(1)",
                  ...(selectedStatus === "reviewed"
                    ? {
                        color: "white",
                        background: "#22c55e",
                        border: "1px solid #22c55e",
                        pointerEvents: "none",
                      }
                    : {
                        color: "#15803d",
                        border: "1px solid #86efac",
                        background: "#dcfce7",
                        ":hover": {
                          color: "white",
                          background: "#22c55e",
                          border: "1px solid #22c55e",
                        },
                      }),
                }}
                size="small"
              />
            </Box>
          </Box>

          {/* Exam & Goal info */}
          <Typography variant="h6" fontSize={"0.8rem"} color="#838383" mb={0}>
            {filters.exam} – {filters.goal} | {filters.section} |{" "}
            {filters.topic} | Subtopic: {filters.subtopic}
          </Typography>
        </CardContent>
        {/* </AccordionSummary> */}

        {/* Accordion details - expanded area */}
        {/* <AccordionDetails>
            <Box sx={{ px: 2, pb: 1 }}>
              <Typography variant="body2" sx={{ color: "#4b5563" }}>
                <b>Status:</b> {cluster.status} • <b>Type:</b> {cluster.type} •{" "}
                <b>LOD:</b> {cluster.lod_index}
              </Typography>

              <Typography variant="body2" sx={{ color: "#4b5563" }}>
                <b>Language:</b> {cluster.primary_language} • <b>SME:</b>{" "}
                {cluster.by_sme ? "Yes" : "No"} •{" "}
                <b>Active:</b> {cluster.is_active ? "✅" : "❌"}
              </Typography>

              <Typography variant="body2" sx={{ color: "#4b5563" }}>
                <b>Time:</b> {cluster.min_time}-{cluster.max_time}s •{" "}
                <b>Exposure:</b> {cluster.exposure_index ?? "N/A"} •{" "}
                <b>Accuracy:</b> {cluster.accuracy_index ?? "N/A"} •{" "}
                <b>Retention:</b> {cluster.retention_index ?? "N/A"}
              </Typography>

              <Typography
                variant="body2"
                sx={{ mt: 1, color: "#6b7280", fontStyle: "italic" }}
              >
                {cluster.description}
              </Typography>

              <Typography
                variant="caption"
                sx={{ display: "block", mt: 1, color: "#9ca3af" }}
              >
                Created: {new Date(cluster.created_at).toLocaleDateString()} •
                Modified: {new Date(cluster.modified_at).toLocaleDateString()}
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion> */}
      </Card>
    </Stack>
  );
}

export default ClusterCard;
